// TRM Exact Fitment V3.0.1 — dynamic ACES dimensions + authoritative matched-key cart proof — 2026-08-18
(() => {
  'use strict';

  if (window.TRMExactFitmentV2?.booted) return;

  const GARAGE_STORAGE_KEY = 'trmSelectedVehicleV1';
  const EXACT_STORAGE_KEY = 'trmExactVehicleV2';
  const OTHER = '__other__';
  const DIMENSIONS = [
    'year', 'make', 'model', 'trim', 'engine', 'drive',
    'cab_type', 'bed_length', 'wheelbase', 'body_type',
    'transmission', 'fuel_type', 'aspiration', 'door_count'
  ];
  const ALL_VALUES = {
    trim: 'all-trims',
    engine: 'all-engines',
    drive: 'all-drive-types',
    cab_type: 'all-cab-types',
    bed_length: 'all-bed-lengths',
    wheelbase: 'all-wheelbases',
    body_type: 'all-body-types',
    transmission: 'all-transmissions',
    fuel_type: 'all-fuel-types',
    aspiration: 'all-aspirations',
    door_count: 'all-door-counts'
  };

  const roots = Array.from(document.querySelectorAll('[data-trm-exact-fitment]'));
  if (!roots.length) return;

  const normalize = (value = '') =>
    value
      .toString()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .toLowerCase()
      .replace(/&/g, 'and')
      .replace(/\+/g, '-plus-')
      .replace(/['’]/g, '')
      .replace(/[^a-z0-9.]+/g, '-')
      .replace(/^-+|-+$/g, '');

  const titleCase = (value = '') =>
    value
      .toString()
      .replace(/-/g, ' ')
      .replace(/\b[a-z]/g, (match) => match.toUpperCase());

  const prettyEngine = (value = '') => {
    if (value === 'all-engines') return 'All Engines';
    return titleCase(value)
      .replace(/\b([0-9]+(?:\.[0-9]+)?)l\b/i, '$1L')
      .replace(/\bV([0-9]+)\b/i, 'V$1')
      .replace(/\bI([0-9]+)\b/i, 'I$1');
  };

  const prettyDrive = (value = '') => {
    if (value === 'all-drive-types') return 'All Drive Types';
    const upper = value.toUpperCase();
    if (['4WD', 'RWD', 'FWD', 'AWD'].includes(upper)) return upper;
    return titleCase(value);
  };

  const labelFor = (dimension, value) => {
    if (!value) return '';
    if (value === OTHER) return 'My option is not listed';
    if (dimension === 'engine') return prettyEngine(value);
    if (dimension === 'drive') return prettyDrive(value);
    if (dimension === 'trim' && value === 'all-trims') return 'All Trims';
    if (dimension === 'bed_length' || dimension === 'wheelbase') {
      return titleCase(value).replace(/-inches?$/i, ' in').replace(/-inch$/i, ' in');
    }
    if (dimension === 'door_count' && /^\d+$/.test(value)) return `${value} Doors`;
    return titleCase(value);
  };

  const parseKey = (key = '') => {
    const parts = key.split('|');
    if (parts.length !== 6) return null;
    const [year, make, model, trim, engine, drive] = parts;
    if (!year || !make || !model || !trim || !engine || !drive) return null;
    return {
      key, year, make, model, trim, engine, drive,
      cab_type: 'all-cab-types',
      bed_length: 'all-bed-lengths',
      wheelbase: 'all-wheelbases',
      body_type: 'all-body-types',
      transmission: 'all-transmissions',
      fuel_type: 'all-fuel-types',
      aspiration: 'all-aspirations',
      door_count: 'all-door-counts'
    };
  };

  const readJson = (root) => {
    try {
      return JSON.parse(root.querySelector('[data-trm-exact-fitment-data]')?.textContent || '{}');
    } catch (error) {
      console.warn('TRM Exact Fitment V2 data could not be parsed.', error);
      return {};
    }
  };

  const readGarage = () => {
    try {
      const raw = window.localStorage.getItem(GARAGE_STORAGE_KEY);
      if (!raw) return null;
      const vehicle = JSON.parse(raw);
      if (!vehicle?.key || !vehicle?.label) return null;
      return {
        ...vehicle,
        makeKey: normalize(vehicle.makeKey || ''),
        modelKey: normalize(vehicle.modelKey || ''),
        key: normalize(vehicle.key || ''),
        label: vehicle.label.toString().trim()
      };
    } catch (_) {
      return null;
    }
  };

  const readExactSaved = () => {
    try {
      const raw = window.localStorage.getItem(EXACT_STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (_) {
      return null;
    }
  };

  const saveExact = (selection) => {
    try {
      const garage = readGarage();

      window.localStorage.setItem(EXACT_STORAGE_KEY, JSON.stringify({
        ...selection,
        garageKey: garage?.key || '',
        savedAt: Date.now()
      }));
    } catch (_) {}
  };

  const garageYearRange = (garage) => {
    if (!garage) return null;

    const years = Array.from(
      `${garage.label || ''} ${garage.key || ''}`.matchAll(/(?:19|20)\d{2}/g),
      (match) => Number(match[0])
    );

    if (!years.length) return null;

    return {
      firstYear: Math.min(...years),
      lastYear: Math.max(...years)
    };
  };

  const recordsForGarageYears = (records, garage) => {
    const range = garageYearRange(garage);
    if (!range) return records;

    return records.filter((record) => {
      const year = Number(record.year);
      return year >= range.firstYear && year <= range.lastYear;
    });
  };

  const savedYearMatchesGarage = (saved, garage) => {
    if (!saved?.year || !garage) return true;

    const range = garageYearRange(garage);
    if (!range) return true;

    const savedYear = Number(saved.year);

    return (
      savedYear >= range.firstYear &&
      savedYear <= range.lastYear
    );
  };

  const garageContextMatches = (context, value) => {
    const normalizedContext = normalize(context || '');
    const normalizedValue = normalize(value || '');

    if (!normalizedContext || !normalizedValue) return false;

    return (
      normalizedContext === normalizedValue ||
      normalizedContext.startsWith(`${normalizedValue}-`) ||
      normalizedContext.endsWith(`-${normalizedValue}`) ||
      normalizedContext.includes(`-${normalizedValue}-`)
    );
  };

  const resolveGarageRecordValue = (
    records,
    dimension,
    garage,
    selectedMake = ''
  ) => {
    if (!garage) return '';

    const scopedRecords = dimension === 'model' && selectedMake
      ? records.filter((record) => record.make === selectedMake)
      : records;

    const candidates = unique(
      scopedRecords.map((record) => record[dimension])
    ).sort((a, b) => b.length - a.length);

    const context = [
      dimension === 'make' ? garage.makeKey : garage.modelKey,
      garage.key,
      garage.label,
      garage.sourceUrl
    ].filter(Boolean).join(' ');

    return candidates.find(
      (candidate) => garageContextMatches(context, candidate)
    ) || '';
  };

  const getVariantId = (root) => {
    const formId = root.dataset.trmProductFormId || '';
    const form = document.getElementById(formId);
    const fromForm = form?.querySelector('input[name="id"]')?.value?.toString() || '';
    return fromForm || root.dataset.trmInitialVariantId || '';
  };

  const unique = (values) => Array.from(new Set((Array.isArray(values) ? values : []).filter(Boolean)));

  const normalizeBoolean = (value) => {
    if (value === true || value === 1) return true;
    if (value === false || value === 0) return false;
    return ['true', '1', 'yes', 'on'].includes(String(value ?? '').trim().toLowerCase());
  };

  const asList = (value) => {
    if (Array.isArray(value)) {
      return value
        .filter((item) => item !== null && item !== undefined && String(item).trim() !== '')
        .map((item) => String(item).trim());
    }
    if (typeof value !== 'string') return [];
    const trimmed = value.trim();
    if (!trimmed) return [];
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed.map((item) => String(item).trim()).filter(Boolean);
      } catch (_) {}
    }
    return trimmed.split(/\r?\n|,\s*/).map((item) => item.trim()).filter(Boolean);
  };

  const buildVariantKeyPool = (variant) => {
    const directKeys = asList(variant?.fitmentKeys);
    const fitmentSets = Array.isArray(variant?.fitmentSets) ? variant.fitmentSets : [];
    const fitmentSetsPresent = normalizeBoolean(variant?.fitmentSetsPresent);
    const expectedReferenceCount = Number(variant?.fitmentSetReferenceCount || 0);
    let resolutionComplete = true;
    const setKeys = [];
    const applications = [];

    if (fitmentSetsPresent && (expectedReferenceCount < 1 || fitmentSets.length !== expectedReferenceCount)) {
      resolutionComplete = false;
    }
    if (!fitmentSetsPresent && fitmentSets.length > 0) resolutionComplete = false;

    fitmentSets.forEach((fitmentSet) => {
      const identity = String(fitmentSet?.id || fitmentSet?.handle || '').trim();
      const keys = asList(fitmentSet?.fitmentKeys);
      const resolved = normalizeBoolean(fitmentSet?.resolved) && Boolean(identity) && keys.length > 0;
      if (!resolved) {
        resolutionComplete = false;
        return;
      }
      setKeys.push(...keys);
      if (Array.isArray(fitmentSet?.applications)) {
        fitmentSet.applications.forEach((application) => {
          if (application && typeof application === 'object') applications.push(application);
        });
      }
    });

    return {
      keys: Array.from(new Set([...directKeys, ...setKeys])),
      applications,
      resolutionComplete,
      expectedReferenceCount,
      resolvedReferenceCount: fitmentSets.filter((set) => normalizeBoolean(set?.resolved)).length
    };
  };

  const currentSelection = (root) => {
    const result = {};
    DIMENSIONS.forEach((dimension) => {
      result[dimension] = root.querySelector(`[data-trm-exact-select="${dimension}"]`)?.value || '';
    });
    return result;
  };

  const exactSelectionLabel = (selection) =>
    DIMENSIONS
      .map((dimension) => ({
        dimension,
        value: selection[dimension],
        label: labelFor(dimension, selection[dimension])
      }))
      .filter(({ dimension, value, label }) =>
        Boolean(label) && value !== ALL_VALUES[dimension]
      )
      .map(({ label }) => label)
      .join(' · ');

  const exactSelectionKey = (selection) =>
    DIMENSIONS.map((dimension) => selection[dimension] || '').join('|');

  const setResult = (root, state, { title, copy, badge, icon, label = '', key = '' } = {}) => {
    root.dataset.state = state;
    if (state === 'confirmed_match') {
      root.classList.remove('is-editing');
    } else {
      root.classList.add('is-editing');
    }

    const box = root.querySelector('[data-trm-exact-result]');
    if (box) box.dataset.state = state;

    const badgeEl = root.querySelector('[data-trm-exact-badge]');
    const titleEl = root.querySelector('[data-trm-exact-result-title]');
    const copyEl = root.querySelector('[data-trm-exact-result-copy]');
    const iconEl = root.querySelector('[data-trm-exact-result-icon]');

    if (badgeEl) badgeEl.textContent = badge || 'Select vehicle';
    if (titleEl) titleEl.textContent = title || 'Complete your vehicle';
    if (copyEl) copyEl.textContent = copy || 'Select the remaining vehicle details to confirm fit.';
    if (iconEl) iconEl.textContent = icon || '?';

    const changeButton = root.querySelector('[data-trm-exact-change-vehicle]');
    if (changeButton) {
      changeButton.textContent = state === 'confirmed_match'
        ? 'Change vehicle'
        : 'Change garage vehicle';
    }

    const guard = document.querySelector(
      `[data-trm-fitment-guard][data-trm-product-form-id="${CSS.escape(root.dataset.trmProductFormId || '')}"]`
    );

    if (guard) {
      guard.dataset.trmExactStatus = state;
      guard.dataset.trmExactLabel = label;
      guard.dataset.trmExactKey = key;
    }

    window.TRMFitmentGuardV1?.update?.();
  };

  const matchingRecords = (records, selection, throughDimension = null) => {
    const stopIndex = throughDimension ? DIMENSIONS.indexOf(throughDimension) : DIMENSIONS.length - 1;
    return records.filter((record) => {
      for (let i = 0; i <= stopIndex; i += 1) {
        const dimension = DIMENSIONS[i];
        const selected = selection[dimension];
        if (!selected || selected === OTHER) continue;
        const allValue = ALL_VALUES[dimension];
        if (allValue && record[dimension] === allValue) continue;
        if (record[dimension] !== selected) return false;
      }
      return true;
    });
  };

  const populateDimension = (root, dimension, records, selection) => {
    const select = root.querySelector(`[data-trm-exact-select="${dimension}"]`);
    if (!select) return;

    const index = DIMENSIONS.indexOf(dimension);
    const priorDimension = index > 0 ? DIMENSIONS[index - 1] : null;
    const candidates = priorDimension
      ? matchingRecords(records, selection, priorDimension)
      : records;

    const values = unique(candidates.map((record) => record[dimension])).sort((a, b) => {
      if (dimension === 'year') return Number(a) - Number(b);
      return labelFor(dimension, a).localeCompare(labelFor(dimension, b));
    });

    const previous = selection[dimension] || '';
    const dimensionLabels = {
      trim: 'trim', cab_type: 'cab type', bed_length: 'bed length',
      wheelbase: 'wheelbase', body_type: 'body type', fuel_type: 'fuel type',
      door_count: 'door count'
    };
    const placeholder = `Select ${dimensionLabels[dimension] || dimension}`;
    select.innerHTML = '';
    select.appendChild(new Option(placeholder, ''));

    values.forEach((value) => {
      select.appendChild(new Option(labelFor(dimension, value), value));
    });

    if (values.length && !values.includes(ALL_VALUES[dimension])) {
      const otherLabel = dimension === 'year'
        ? 'My year is not listed'
        : dimension === 'engine'
          ? 'My engine is not listed'
          : dimension === 'trim'
            ? 'My trim is not listed'
            : dimension === 'drive'
              ? 'My drive type is not listed'
              : `My ${dimension} is not listed`;
      select.appendChild(new Option(otherLabel, OTHER));
    }

    select.disabled = values.length === 0;

    const field = select.closest('.trm-exact-fitment__field');
    const universalOnly = Boolean(
      ALL_VALUES[dimension] &&
      values.length === 1 &&
      values[0] === ALL_VALUES[dimension]
    );
    field?.classList.toggle('is-universal', universalOnly);

    if (previous && (values.includes(previous) || previous === OTHER)) {
      select.value = previous;
    } else if (ALL_VALUES[dimension] && values.length === 1 && values[0] === ALL_VALUES[dimension]) {
      select.value = values[0];
    } else {
      select.value = '';
    }
  };

  const applyGaragePrefill = (root, records) => {
    const garage = readGarage();
    const garageBox = root.querySelector('[data-trm-exact-garage]');
    const garageLabel = root.querySelector('[data-trm-exact-garage-label]');

    if (garage?.label) {
      if (garageBox) garageBox.hidden = false;
      if (garageLabel) garageLabel.textContent = garage.label;
    } else if (garageBox) {
      garageBox.hidden = true;
    }

    const selection = currentSelection(root);
    const garageRecords = recordsForGarageYears(
      records,
      garage
    );

    const garageMake = resolveGarageRecordValue(
      garageRecords,
      'make',
      garage
    );

    if (garageMake) selection.make = garageMake;

    const garageModel = resolveGarageRecordValue(
      garageRecords,
      'model',
      garage,
      selection.make
    );

    if (garageModel) selection.model = garageModel;

    const saved = readExactSaved();
    const savedMatchesRecords = Boolean(
      saved &&
      garageRecords.some((record) => {
        return DIMENSIONS.every((dimension) => {
          const savedValue = normalize(saved[dimension] || '');
          const allValue = ALL_VALUES[dimension];

          return (
            record[dimension] === savedValue ||
            Boolean(allValue && record[dimension] === allValue)
          );
        });
      })
    );

    const savedMatchesGarage = Boolean(
      saved &&
      savedMatchesRecords &&
      (
        !garage ||
        Boolean(garageMake && garageModel)
      ) &&
      (
        !garage ||
        normalize(saved.make || '') === garageMake
      ) &&
      (
        !garage ||
        normalize(saved.model || '') === garageModel
      ) &&
      savedYearMatchesGarage(saved, garage)
    );

    if (savedMatchesGarage) {
      DIMENSIONS.forEach((dimension) => {
        const value = saved[dimension];
        if (!value || value === OTHER) return;
        if (records.some((record) => record[dimension] === value)) {
          if (dimension === 'make' && selection.make && selection.make !== value) return;
          if (dimension === 'model' && selection.model && selection.model !== value) return;
          selection[dimension] = value;
        }
      });
    }

    return selection;
  };

  const rebuild = (root, records, initial = false) => {
    const targetSelection = initial
      ? applyGaragePrefill(root, records)
      : currentSelection(root);

    DIMENSIONS.forEach((dimension, index) => {
      const actualSelection = currentSelection(root);
      const selectionForDimension = {
        ...targetSelection
      };

      DIMENSIONS.slice(0, index).forEach((priorDimension) => {
        selectionForDimension[priorDimension] =
          actualSelection[priorDimension];
      });

      populateDimension(
        root,
        dimension,
        records,
        selectionForDimension
      );
    });

    evaluateRoot(root, records);
  };

  const evaluateRoot = (root, records) => {
    const selection = currentSelection(root);

    if (DIMENSIONS.some((dimension) => selection[dimension] === OTHER)) {
      const label = exactSelectionLabel(selection);
      setResult(root, 'mismatch', {
        badge: "Doesn't fit",
        icon: '×',
        title: "This part doesn't fit this vehicle",
        copy: "This option isn't listed for this part. Change your vehicle or ask us for help.",
        label,
        key: ''
      });
      return;
    }

    const incomplete = DIMENSIONS.some((dimension) => !selection[dimension]);
    if (incomplete) {
      setResult(root, 'selection_required', {
        badge: 'Select vehicle',
        icon: '?',
        title: 'Complete your vehicle',
        copy: 'Select the remaining vehicle details to confirm this part fits.',
        label: exactSelectionLabel(selection),
        key: ''
      });
      return;
    }

    const selectedKey = exactSelectionKey(selection);
    const exactMatch = records.find((record) => {
      return DIMENSIONS.every((dimension) => {
        const allValue = ALL_VALUES[dimension];
        return record[dimension] === selection[dimension] || (allValue && record[dimension] === allValue);
      });
    });

    const label = exactSelectionLabel(selection);

    if (exactMatch) {
      const authoritativeKey = exactMatch.key || selectedKey;
      saveExact(selection);
      setResult(root, 'confirmed_match', {
        badge: 'Fit confirmed',
        icon: '✓',
        title: 'Fits your vehicle',
        copy: label,
        label,
        key: authoritativeKey
      });
    } else {
      setResult(root, 'mismatch', {
        badge: "Doesn't fit",
        icon: '×',
        title: "This part doesn't fit this vehicle",
        copy: `${label} is not listed as compatible with this part.`,
        label,
        key: selectedKey
      });
    }
  };

  const setLegacyGuardVisibility = (root, exactActive) => {
    const guard = document.querySelector(
      `[data-trm-fitment-guard][data-trm-product-form-id="${CSS.escape(root.dataset.trmProductFormId || '')}"]`
    );
    if (!guard) return;
    guard.hidden = exactActive;
    if (!exactActive) {
      delete guard.dataset.trmExactStatus;
      delete guard.dataset.trmExactLabel;
      delete guard.dataset.trmExactKey;
    }
  };

  const activateVariant = (root) => {
    const data = readJson(root);
    const variantId = getVariantId(root);
    const variant = data?.variants?.[variantId];
    const keyPool = buildVariantKeyPool(variant);
    const structuredRecords = keyPool.applications.map((application) => {
      const record = {};
      DIMENSIONS.forEach((dimension) => {
        record[dimension] = normalize(application[dimension] || ALL_VALUES[dimension] || '');
      });
      record.key = application.key || '';
      return record;
    }).filter((record) => DIMENSIONS.slice(0, 6).every((dimension) => record[dimension]));
    const records = structuredRecords.length
      ? structuredRecords
      : keyPool.keys.map(parseKey).filter(Boolean);

    // READY controls which guard has authority. If the referenced fitment-set
    // data cannot be read in Liquid, do not fall back to the permissive legacy
    // Vehicle Group guard. Show the exact panel and fail closed instead.
    const initialVariantId = root.dataset.trmInitialVariantId || '';
    const serverReadyFallback =
      variantId === initialVariantId && root.dataset.trmServerReady === 'true';
    const exactReady = Boolean(variant?.ready || serverReadyFallback);

    root.hidden = !exactReady;
    setLegacyGuardVisibility(root, exactReady);

    if (!exactReady) {
      window.TRMFitmentGuardV1?.update?.();
      return;
    }

    root.dataset.variantId = variantId;

    if (!keyPool.resolutionComplete || !records.length) {
      setResult(root, 'selection_required', {
        badge: 'Fitment unavailable',
        icon: '!',
        title: "We can't confirm fitment online",
        copy: 'Please contact us before ordering this part.',
        label: '',
        key: ''
      });
      return;
    }

    rebuild(root, records, true);
  };

  roots.forEach((root) => {
    root.addEventListener('change', (event) => {
      if (!event.target.matches('[data-trm-exact-select]')) return;
      const data = readJson(root);
      const variantId = getVariantId(root);
      const variant = data?.variants?.[variantId];
      const keyPool = buildVariantKeyPool(variant);
      const structuredRecords = keyPool.applications.map((application) => {
        const record = {};
        DIMENSIONS.forEach((dimension) => {
          record[dimension] = normalize(application[dimension] || ALL_VALUES[dimension] || '');
        });
        record.key = application.key || '';
        return record;
      }).filter((record) => DIMENSIONS.slice(0, 6).every((dimension) => record[dimension]));
      const records = structuredRecords.length
        ? structuredRecords
        : keyPool.keys.map(parseKey).filter(Boolean);
      if (!keyPool.resolutionComplete || !records.length) {
        activateVariant(root);
        return;
      }
      rebuild(root, records, false);
    });

    root.querySelector('[data-trm-exact-change-vehicle]')?.addEventListener('click', (event) => {
      event.preventDefault();

      if (
        root.dataset.state === 'confirmed_match' &&
        !root.classList.contains('is-editing')
      ) {
        root.classList.add('is-editing');
        event.currentTarget.textContent = 'Change garage vehicle';
        root.querySelector('[data-trm-exact-select="year"]')?.focus();
        return;
      }

      if (window.TRMGarageV1?.open) {
        window.TRMGarageV1.open(event.currentTarget);
      } else {
        window.dispatchEvent(new CustomEvent('trm:garage-open', { detail: { trigger: event.currentTarget } }));
      }
    });

    activateVariant(root);
  });

  const refreshAll = () => roots.forEach(activateVariant);

  document.addEventListener('change', (event) => {
    if (event.target.matches('input[name="id"], select[name="id"], [data-option-value-id]')) {
      window.setTimeout(refreshAll, 50);
    }
  }, true);

  ['variant:change', 'variant-change', 'product:variant-change'].forEach((eventName) => {
    document.addEventListener(eventName, () => window.setTimeout(refreshAll, 25));
  });

  window.addEventListener('trm:vehicle-changed', refreshAll);
  window.addEventListener('trm:vehicle-cleared', refreshAll);
  window.addEventListener('storage', (event) => {
    if ([GARAGE_STORAGE_KEY, EXACT_STORAGE_KEY].includes(event.key)) refreshAll();
  });

  window.TRMExactFitmentV2 = {
    booted: true,
    version: '3.0.1',
    refresh: refreshAll,
    readGarage,
    readExactVehicle: readExactSaved
  };
})();
